import app from "@/main";

/**
 * Takip servisi - Takip verilerini yönetir
 * Önbellekleme ve hata yönetimi özellikleri içerir
 */
class TakipEkleService {
  /**
   * Takip kaydını ID'ye göre getirir
   */
  async getTakipById(id: string) {
    if (!id) throw new Error('Takip ID boş olamaz');
    return app.$http.get(`/api/v1/takip/${id}`);
  }

  /**
   * Takip ID'sine göre alacaklıları getirir
   */
  async getAlacaklilarByTakipId(id: string) {
    if (!id) return { isGetted: true, data: [] };
    const res = await app.$http.get(`/api/v1/takip/${id}/alacaklilar`);
    return { isGetted: true, data: Array.isArray(res.data) ? res.data : (res.data.alacaklilar || []) };
  }

  /**
   * Takip ID'sine göre borçluları getirir
   */
  async getBorcluByTakipId(id: string) {
    if (!id) return { isGetted: true, data: [] };
    const res = await app.$http.get(`/api/v1/takip/${id}/borclular`);
    return { isGetted: true, data: Array.isArray(res.data) ? res.data : (res.data.borclular || []) };
  }

  /**
   * Takip ID'sine göre alacakları getirir
   */
  async getAlacaklarByTakipId(id: string) {
    if (!id) return { isGetted: true, data: [] };
    const res = await app.$http.get(`/api/v1/takip/${id}/alacaklar`);
    return { isGetted: true, data: Array.isArray(res.data) ? res.data : (res.data.alacaklar || []) };
  }

  /**
   * Takip ID'sine göre iş listesini getirir
   */
  async getIsListesiByTakipId(id: string) {
    if (!id) return { isGetted: true, data: [] };
    const res = await app.$http.get(`/api/v1/takip/${id}/is-listesi`);
    return { isGetted: true, data: Array.isArray(res.data) ? res.data : (res.data.isListesi || []) };
  }

  /**
   * Takip ID'sine göre faiz verisini getirir
   */
  async getFaizDataByTakipId(id: string) {
    if (!id) throw new Error('Takip ID boş olamaz');
    
    try {
      // Faiz hesaplama isteği
      const res = await app.$http.post(`/api/v1/takip/${id}/faiz`, {
        // İstek parametrelerini ekleyelim
        hesapTarihi: new Date().toISOString().split('T')[0], // Bugünün tarihi
        includeDetails: true // Detayları da getir
      });
      
      return res.data;
    } catch (error) {
      console.error('Faiz verisi alınırken hata:', error);
      return {
        hesaplanabilir: false,
        data: { message: 'Faiz hesaplama isteği başarısız oldu. Lütfen tekrar deneyin.' }
      };
    }
  }

  /**
   * Takip ile ilgili tüm verileri tek seferde getirir
   */
  async getTakipTumVeriler(id: string) {
    if (!id) {
      return {
        takip: null,
        alacaklilar: { isGetted: true, data: [] },
        borclular: { isGetted: true, data: [] },
        alacaklar: { isGetted: true, data: [] },
        isListesi: { isGetted: true, data: [] }
      };
    }
    const takipRes = await this.getTakipById(id).catch(() => null);
    const [alacaklilarRes, borclularRes, alacaklarRes, isListesiRes] = await Promise.all([
      this.getAlacaklilarByTakipId(id).catch(() => ({ isGetted: true, data: [] })),
      this.getBorcluByTakipId(id).catch(() => ({ isGetted: true, data: [] })),
      this.getAlacaklarByTakipId(id).catch(() => ({ isGetted: true, data: [] })),
      this.getIsListesiByTakipId(id).catch(() => ({ isGetted: true, data: [] })),
    ]);
    return {
      takip: takipRes ? takipRes.data : null,
      alacaklilar: alacaklilarRes,
      borclular: borclularRes,
      alacaklar: alacaklarRes,
      isListesi: isListesiRes
    };
  }

  getTakipListesi() {
    return app.$http.get(`/api/v1/takipler`);
  }

  async updateTakipBilgileri(payload: any) {
    return app.$http.put(`/api/v1/takip/${payload.id}`, payload);
  }

  async addAlacakli(payload: any) {
    return app.$http.post(`/api/v1/takip/${payload.takipId}/alacaklilar`, payload);
  }

  async removeAlacakli(payload: any) {
    return app.$http.delete(`/api/v1/takip/${payload.takipId}/alacaklilar/${payload.alacakliId}`);
  }

  async addBorclu(payload: any) {
    return app.$http.post(`/api/v1/takip/${payload.takipId}/borclular`, payload);
  }

  async removeBorclu(payload: any) {
    return app.$http.delete(`/api/v1/takip/${payload.takipId}/borclular/${payload.borcluId}`);
  }

  async addAlacak(payload: any) {
    return app.$http.post(`/api/v1/takip/${payload.takipId}/alacaklar`, payload);
  }

  async removeAlacak(payload: any) {
    return app.$http.delete(`/api/v1/takip/${payload.takipId}/alacaklar/${payload.alacakId}`);
  }

  async updateAlacak(payload: any) {
    return app.$http.put(`/api/v1/takip/${payload.takipId}/alacaklar/${payload.alacakId}`, payload);
  }

  async copyAlacak(payload: any) {
    return app.$http.post(`/api/v1/takip/${payload.takipId}/alacaklar/${payload.alacakId}/copy`, payload);
  }

  async updateAltTipHesap(payload: any) {
    return app.$http.put(`/api/v1/takip/${payload.takipId}/alt-tip-hesap`, payload);
  }

  /**
   * Takip talebini günceller
   */
  async updateTakipTalebi(payload: any) {
    if (!payload.id) throw new Error('Takip talebi ID boş olamaz');
    return app.$http.put(`/api/v1/takip-talebi/${payload.id}`, payload);
  }

  async addTahsilat(payload: any) {
    return app.$http.post(`/api/v1/takip/${payload.takipId}/tahsilatlar`, payload);
  }

  async addMasraf(payload: any) {
    return app.$http.post(`/api/v1/takip/${payload.takipId}/masraflar`, payload);
  }

  /**
   * İş listesine yeni iş ekler
   */
  async addIsListesi(payload: any) {
    return app.$http.post(`/api/v1/takip/${payload.takipId}/is-listesi`, payload);
  }

  /**
   * İş listesindeki bir işi günceller
   */
  async updateIsListesi(payload: any) {
    return app.$http.put(`/api/v1/takip/${payload.takipId}/is-listesi/${payload.isId}`, payload);
  }

  /**
   * İş listesinden bir işi siler
   */
  async removeIsListesi(payload: any) {
    return app.$http.delete(`/api/v1/takip/${payload.takipId}/is-listesi/${payload.isId}`);
  }

  /**
   * Borçlu ID'sine göre vekilleri getirir
   */
  async getVekillerByBorcluId(borcluId: number) {
    if (!borcluId) return [];
    try {
      const res = await app.$http.get(`/api/v1/borclu/${borcluId}/borclu-vekili`);
      return Array.isArray(res.data) ? res.data : (res.data.vekiller || []);
    } catch (error) {
      console.error(`Borçlu ${borcluId} için vekiller alınırken hata:`, error);
      return [];
    }
  }

  /**
   * Borçlu ID'sine göre tebligatları getirir
   */
  async getTebligatlarByBorcluId(borcluId: number) {
    if (!borcluId) return [];
    try {
      const res = await app.$http.get(`/api/v1/borclu/${borcluId}/tebligatlar`);
      return Array.isArray(res.data) ? res.data : (res.data.tebligatlar || []);
    } catch (error) {
      console.error(`Borçlu ${borcluId} için tebligatlar alınırken hata:`, error);
      return [];
    }
  }

  /**
   * Borçlu ID'sine göre borçlu bilgilerini getirir
   */
  async getBorcluBilgileriById(borcluId: number) {
    if (!borcluId) return null;
    try {
      const res = await app.$http.get(`/api/v1/borclu/${borcluId}`);
      return res.data?.borclu_bilgileri || null;
    } catch (error) {
      console.error(`Borçlu ${borcluId} için bilgiler alınırken hata:`, error);
      return null;
    }
  }

  /**
   * Borçlu ID'sine göre adresleri getirir
   */
  async getAdreslerByBorcluId(borcluId: number) {
    if (!borcluId) return { adresler: [], varsayilan_adres: null };
    try {
      const res = await app.$http.get(`/api/v1/borclu/${borcluId}/adresler`);
      // API response: { data: { adresler: [...], varsayilan_adres: {...} } }
      const responseData = res.data?.data || res.data;
      return {
        adresler: Array.isArray(responseData?.adresler) ? responseData.adresler : [],
        varsayilan_adres: responseData?.varsayilan_adres || null
      };
    } catch (error) {
      console.error(`Borçlu ${borcluId} için adresler alınırken hata:`, error);
      return { adresler: [], varsayilan_adres: null };
    }
  }
}

export default new TakipEkleService(); 